/**
 * web/src/api/speakerBatch.ts
 * Typed client for the speaker batch fan-out endpoints (server:
 * src/pixel/api/speakers.py, contract in docs/DESIGN-speaker-entity-and-batch-2026-08.md §2).
 *
 * Batch is orchestration over the existing per-speaker endpoints — NEVER a
 * second engine. Each endpoint takes an event + a list of monday item ids and
 * fans out to the single-speaker action, returning one outcome per item
 * (mirrors start-bulk's per-item shape). The UI shows a results summary and,
 * for `release`, walks the freshly-opened gates through the Batch Review stack.
 */
import { api } from '../lib/api';

/**
 * One item's outcome from a batch fan-out (server contract §2:
 * `{itemId, outcome, detail?}`). `ok` = the action ran; `skipped` = a
 * documented no-op (no headshot, generation already running, gate already
 * approved, step not armed, …); `error` = the per-item call failed. `detail`
 * is a short human sentence explaining a skip/error.
 */
export interface BatchItemResult {
  itemId: string;
  outcome: 'ok' | 'skipped' | 'error';
  detail?: string | null;
  /** Present on a `release` outcome so the UI can build the review stack. */
  workflowId?: string | null;
  /** Present on a `release` outcome — the opened gate's zero-based step index. */
  stepIndex?: number | null;
}

/** Envelope for every batch endpoint: the per-item results, in request order. */
export interface BatchOutcome {
  results: BatchItemResult[];
}

/**
 * Fans out speaker-card generation to each selected speaker (§2). Skips a
 * speaker with no headshot, one already generating, or one whose card gate is
 * already approved — those come back `outcome:'skipped'` with a `detail`.
 *
 * @param eventId the roster's event
 * @param itemIds selected monday item ids
 * @returns BatchOutcome — one result per requested item
 * @throws ApiClientError on non-2xx (404 flag off / EVENT_NOT_FOUND)
 * @sideEffects HTTP POST; kicks off the card pipeline per eligible speaker
 * @idempotent yes — a speaker already generating/approved is skipped, not re-run
 */
export async function batchGenerateCards(
  eventId: string,
  itemIds: string[],
): Promise<BatchOutcome> {
  return api<BatchOutcome>('/api/speakers/batch/generate-cards', {
    method: 'POST',
    body: JSON.stringify({ eventId, itemIds }),
  });
}

/**
 * Releases a date-armed / held band-B gate NOW for each selected speaker (§2)
 * — extends the single release (POST /api/workflows/{id}/release/{step_id}) to
 * a batch. Releasing a `scheduled` step clears its `scheduledFor` and opens the
 * gate; idempotent per item (an already-open gate comes back `ok`/`skipped`).
 * The `ok` outcomes carry `{workflowId, stepIndex}` so the caller can navigate
 * into the Batch Review stack for exactly the gates this call opened.
 *
 * @param eventId the roster's event
 * @param itemIds selected monday item ids
 * @param stepId the band-B step to release (e.g. 'logistics', 'thank_you')
 * @returns BatchOutcome — one result per requested item
 * @throws ApiClientError on non-2xx (404 flag off / EVENT_NOT_FOUND)
 * @sideEffects HTTP POST; opens the named gate per eligible speaker
 * @idempotent yes — an already-open gate is a no-op
 */
export async function batchRelease(
  eventId: string,
  itemIds: string[],
  stepId: string,
): Promise<BatchOutcome> {
  return api<BatchOutcome>('/api/speakers/batch/release', {
    method: 'POST',
    body: JSON.stringify({ eventId, itemIds, stepId }),
  });
}

// ---------------------------------------------------------------------------
// Async batch RUNS (design: docs/design_handoff_speaker_batch). A run executes
// the SAME per-speaker fan-out in the background so the dashboard can show live
// progress, a persistent dock, and a retry-failed affordance. Still no send: a
// card run opens the illustration gate, a logistics run opens the logistics
// gate for review. Server: pixel.api.speakers (POST/GET /api/speakers/batch/runs).
// ---------------------------------------------------------------------------

/** Which bulk action a run performs. The `card_*` trio are the cards-board
 * stage advances (finalize the picked draft / build the card / publish). */
export type BatchRunAction =
  | 'cards'
  | 'logistics'
  | 'promotion'
  | 'prep_notes'
  | 'thank_you'
  | 'card_finalize'
  | 'card_build'
  | 'card_publish'
  /** Bulk redraft of open email gates (Speaker Approvals "Regenerate all").
   * Each item names its gate in params ({workflowId, stepIndex}); no stepId. */
  | 'regenerate';

/** One speaker's live state within a run. */
export interface BatchRunItem {
  itemId: string;
  name: string;
  org: string;
  status: 'queued' | 'running' | 'done' | 'failed';
  reason?: string | null;
  /** Present once done — the gate this item opened, for the review-stack handoff. */
  workflowId?: string | null;
  stepIndex?: number | null;
}

/** A run's full state, polled for live progress. */
export interface BatchRun {
  jobId: string;
  action: BatchRunAction;
  eventId: string;
  stepId?: string | null;
  state: 'running' | 'done';
  createdAt: string;
  items: BatchRunItem[];
}

/** A speaker to include in a run (display fields ride along; server owns status). */
export interface BatchRunSeed {
  itemId: string;
  name: string;
  org: string;
  /** Per-item action params (e.g. card_finalize's chosenCandidate/highlightColor,
   * card_publish's chosenCandidate). Forwarded verbatim to the per-item worker. */
  params?: Record<string, string>;
}

/**
 * Starts a background batch run and returns its initial (all-queued) state.
 * Poll {@link getBatchRun} for progress. `stepId` is required for `logistics`.
 *
 * @sideEffects HTTP POST; spawns a background fan-out (card gen / gate release)
 * @idempotent no — each call is a new run (per-item work stays idempotent)
 */
export async function startBatchRun(
  action: BatchRunAction,
  eventId: string,
  items: BatchRunSeed[],
  stepId?: string,
): Promise<BatchRun> {
  return api<BatchRun>('/api/speakers/batch/runs', {
    method: 'POST',
    body: JSON.stringify({ action, eventId, items, ...(stepId ? { stepId } : {}) }),
  });
}

/** Polls one run's live state (per-item status + done/total). */
export async function getBatchRun(jobId: string): Promise<BatchRun> {
  return api<BatchRun>(`/api/speakers/batch/runs/${encodeURIComponent(jobId)}`);
}

/** Still-running runs for an event — the dock re-hydrates from this on reload. */
export async function listActiveBatchRuns(eventId: string): Promise<BatchRun[]> {
  const res = await api<{ runs: BatchRun[] }>(
    `/api/speakers/batch/runs?eventId=${encodeURIComponent(eventId)}`,
  );
  return res.runs;
}

/** Re-queues a run's failed items and re-runs them in the background. */
export async function retryBatchRun(jobId: string): Promise<BatchRun> {
  return api<BatchRun>(`/api/speakers/batch/runs/${encodeURIComponent(jobId)}/retry`, {
    method: 'POST',
  });
}

// ---------------------------------------------------------------------------
// Logistics multi-session preview — flags, at selection time, a speaker booked
// on >1 agenda session (the email shows only one). The review gate is the second
// catch. Server reads the agenda once and returns every session per item.
// ---------------------------------------------------------------------------

/** One agenda session (subset of PoE fields the drawer/banner render). */
export interface PreviewSession {
  title?: string | null;
  day?: string | null;
  time?: string | null;
  stage?: string | null;
}

/** The sessions found for one speaker. */
export interface SpeakerSessionsPreview {
  itemId: string;
  sessions: PreviewSession[];
}

/**
 * Fetches PoE agenda sessions for a set of speakers (the logistics drawer's
 * multi-session flag). Read-only, best-effort — a speaker with no PoE match or
 * a Sheets failure simply comes back with an empty session list.
 */
export async function sessionsPreview(
  eventId: string,
  speakers: { itemId: string; name: string }[],
): Promise<SpeakerSessionsPreview[]> {
  const res = await api<{ previews: SpeakerSessionsPreview[] }>(
    '/api/speakers/batch/sessions-preview',
    { method: 'POST', body: JSON.stringify({ eventId, speakers }) },
  );
  return res.previews;
}
